<template>
  <div class="tenant-rating" @click="onPageClick">
    <div class="content">
      <div class="prop-selector">
        <div class="prop-card" @click.stop="toggleDropdown">
          <div class="prop-info">
            <h2 class="prop-name">
              {{ isEn ? (selectedProp.nameEn || selectedProp.name) : selectedProp.name }}
              <van-icon name="arrow-down" class="expand-icon" />
            </h2>
          </div>
        </div>

        <div class="prop-dropdown" v-if="showDropdown" @click.stop>
        <div
          class="dropdown-item"
          v-for="item in propList"
          :key="item.buildingId"
          @click.stop="selectProp(item)"
        >
          <div class="dropdown-info">
            <div class="dropdown-name">{{ isEn ? (item.nameEn || item.name) : item.name }}</div>
          </div>
          <van-icon v-if="selectedProp.buildingId === item.buildingId" name="check" class="check-icon" />
        </div>
      </div>
      </div>

      <div class="rating-sections">
        <div class="rating-section">
          <div class="section-header">
            <h3>{{ $t('tenantRating.livingExperience') }}</h3>
          </div>
          <div class="rating-options">
            <button
              class="rating-btn"
              :class="{ active: ratings.experience === option.value }"
              v-for="option in ratingOptions"
              :key="option.value"
              @click="setRating('experience', option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="rating-section">
          <div class="section-header">
            <h3>{{ $t('tenantRating.cleanliness') }}</h3>
          </div>
          <div class="rating-options">
            <button
              class="rating-btn"
              :class="{ active: ratings.cleanliness === option.value }"
              v-for="option in ratingOptions"
              :key="option.value"
              @click="setRating('cleanliness', option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="rating-section">
          <div class="section-header">
            <h3>{{ $t('tenantRating.serviceAttitude') }}</h3>
          </div>
          <div class="rating-options">
            <button
              class="rating-btn"
              :class="{ active: ratings.service === option.value }"
              v-for="option in ratingOptions"
              :key="option.value"
              @click="setRating('service', option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="bottom-bar">
      <button class="submit-btn" @click="submitRating">
        <span>{{ $t('tenantRating.submitReview') }}</span>
        <van-icon name="arrow-right" />
      </button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { formatBuildingAddress } from './addressUtil'

export default {
  data() {
    return {
      showDropdown: false,
      selectedProp: {
        buildingId: null,
        name: '',
        nameEn: ''
      },
      propList: [],
      ratings: {
        experience: 0,
        cleanliness: 0,
        service: 0
      }
    }
  },
  computed: {
    isEn() {
      return Vue.config.lang === 'en-US'
    },
    ratingOptions() {
      return [
        { value: 1, label: this.$t('tenantRating.poor') },
        { value: 2, label: this.$t('tenantRating.needsImprovement') },
        { value: 3, label: this.$t('tenantRating.satisfied') },
        { value: 4, label: this.$t('tenantRating.veryGood') },
        { value: 5, label: this.$t('tenantRating.excellent') }
      ]
    }
  },
  created() {
    const { buildingId, name } = this.$route.query
    if (buildingId) {
      this.selectedProp.buildingId = Number(buildingId)
      if (name) this.selectedProp.name = name
    }
    this.getHouseList()
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
    },
    onPageClick() {
      this.showDropdown = false
    },
    selectProp(item) {
      this.selectedProp = { ...item }
      this.showDropdown = false
      this.loadMyRating()
    },
    loadMyRating() {
      if (!this.selectedProp.buildingId) return
      this.ratings = { experience: 0, cleanliness: 0, service: 0 }
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/score/myRating'),
        method: 'get',
        params: this.$httpCustApp.adornParams({
          buildingId: this.selectedProp.buildingId
        })
      }).then(({ data }) => {
        if (data.code === 0 && data.rating) {
          this.ratings.experience = data.rating.livingScore / 20
          this.ratings.cleanliness = data.rating.safetyScore / 20
          this.ratings.service = data.rating.attitudeScore / 20
        }
      })
    },
    getHouseList() {
      this.$http({
        url: this.$http.adornUrl('/wxapp/building/house/list'),
        method: 'GET',
        params: this.$http.adornParams({
          page: 1,
          limit: 9999,
          userId: this.$userId,
          orgId: this.$orgId,
          loginUserId: this.$globalData.userInfo.userId,
          loginUserOrgId: this.$globalData.userInfo.orgId
        })
      }).then(({ data }) => {
        if (data && data.code === 0) {
          const seen = new Set()
          const tenantList = data.page.list
            .filter(item => !seen.has(item.buildingId) && seen.add(item.buildingId))
            .map(item => ({
              buildingId: item.buildingId,
              name: formatBuildingAddress(item.subareaName, item.buildingName, false),
              nameEn: formatBuildingAddress(item.subareaName, item.buildingName, true)
            }))
          this.propList = tenantList
          const queryId = this.selectedProp.buildingId
          if (queryId) {
            const matched = tenantList.find(item => item.buildingId === queryId)
            if (matched) {
              this.selectedProp = { ...matched }
            }
          } else if (tenantList.length > 0) {
            this.selectedProp = { ...tenantList[0] }
          }
          this.loadMyRating()
        }
      })
    },
    setRating(type, value) {
      this.ratings[type] = value
    },
    submitRating() {
      if (!this.selectedProp.buildingId) {
        this.$toast.fail(this.$t('tenantRating.missingProperty'))
        return
      }
      if (!this.ratings.experience || !this.ratings.cleanliness || !this.ratings.service) {
        this.$toast.fail(this.isEn ? 'Please rate all categories' : '请完成所有评分')
        return
      }
      this.$toast.loading({ duration: 0, message: this.$t('tenantRating.submitting'), forbidClick: true })
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/score/submit'),
        method: 'post',
        data: this.$httpCustApp.adornData({
          buildingId: this.selectedProp.buildingId,
          livingScore: this.ratings.experience * 20,
          safetyScore: this.ratings.cleanliness * 20,
          attitudeScore: this.ratings.service * 20
        })
      }).then(({ data }) => {
        this.$toast.clear()
        if (data.code === 0) {
          this.$toast.success({ duration: 1500, forbidClick: true, message: this.$t('tenantRating.submitSuccess') })
          setTimeout(() => {
            this.$router.go(-1)
          }, 1500)
        } else {
          this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || this.$t('tenantRating.submitFail') })
        }
      }).catch(() => {
        this.$toast.clear()
        this.$toast.fail({ duration: 1500, forbidClick: true, message: this.$t('tenantRating.submitFail') })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.tenant-rating {
  min-height: 100vh;
  background: linear-gradient(180deg, #c3d2f9 0%, #eef4ff 48%, #ffffff 100%);
  padding-bottom: 140px;

  .content {
    padding: 29px;
  }

  .prop-selector {
    position: relative;
  }

  .prop-card {
    display: flex;
    gap: 18px;
    align-items: center;
    background: #fff;
    border-radius: 29px;
    padding: 22px;
    box-shadow: 0 7px 14px rgba(0, 0, 0, 0.08);
    border: 2px solid rgba(170, 173, 176, 0.05);

    .prop-info {
      flex: 1;

      .prop-name {
        font-size: 28px;
        font-weight: 800;
        color: #0b1f3f;
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 5px;

        .expand-icon {
          font-size: 24px;
          color: #9b9b9b;
        }
      }

    }
  }

  .prop-dropdown {
    position: absolute;
    top: calc(100% + 15px);
    left: 0;
    right: 0;
    z-index: 10;
    background: #fff;
    border-radius: 29px;
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
    border: 2px solid rgba(170, 173, 176, 0.05);
    overflow: hidden;

    .dropdown-item {
      display: flex;
      align-items: center;
      gap: 15px;
      padding: 20px 25px;
      border-bottom: 2px solid #f5f5f5;

      &:last-child {
        border-bottom: 0;
      }

      &:active {
        background: #f8f9fa;
      }

      .dropdown-info {
        flex: 1;

        .dropdown-name {
          font-size: 24px;
          font-weight: 700;
          color: #0b1f3f;
        }

      }

      .check-icon {
        font-size: 24px;
        color: #1e5bff;
      }
    }
  }

  .rating-sections {
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .rating-section {
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding-bottom: 15px;
      border-bottom: 2px solid #f0f0f0;
      margin-bottom: 20px;

      h3 {
        font-size: 28px;
        font-weight: 800;
        color: #0b1f3f;
        margin: 0;
      }

    }

    .rating-options {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .rating-btn {
        padding: 15px 20px;
        border-radius: 9999px;
        border: 2px solid #e0e0e0;
        background: #fff;
        font-size: 22px;
        color: #666;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        text-align: center;

        &:active {
          transform: scale(0.95);
        }

        &.active {
          background: linear-gradient(116deg, #1e5bff 20%, #4a7eff 94%);
          border-color: #1e5bff;
          color: #fff;
          box-shadow: 0 7px 14px rgba(30, 91, 255, 0.3);
        }
      }
    }
  }

  .bottom-bar {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 750px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-top: 2px solid rgba(226, 232, 240, 0.5);
    padding: 20px 29px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom));

    .submit-btn {
      width: 100%;
      height: 80px;
      border-radius: 9999px;
      background: linear-gradient(116deg, #1e5bff 20%, #4a7eff 94%);
      border: 0;
      color: #fff;
      font-size: 28px;
      font-weight: 800;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      box-shadow: 0 14px 28px rgba(30, 91, 255, 0.3);
      transition: all 0.2s;

      &:active {
        transform: scale(0.98);
      }
    }
  }

}
</style>
